import { getLatamDictionary } from '@/datafiles/languages/latam';
import { getPathFor } from '@/datafiles/i18n/translatedPages';
import { getOpendockUrl } from '@/datafiles/i18n/opendock';

// Page IDs come from datafiles/i18n/pages.json. LATAM pages that are not
// built yet (`live: false`) are still linked so the header is complete.
const pathOf = (id, locale) => getPathFor(id, locale, { includePlanned: true });

const LEGACY_LEGAL_LOCALE = {
  'en-LATAM': 'en',
  'pt-BR': 'pt',
  'es-MX': 'es',
  'es-CO': 'es',
};

const stripSlash = (path) => path.replace(/\/$/, '');

export function buildLatamChrome(locale) {
  const { common } = getLatamDictionary(locale);
  const { nav, footer } = common;

  // The LATAM home is the AI page, like the US site; the previous home now lives
  // at the "platform" page.
  const homeUrl = pathOf('latam-home', locale);
  const aiUrl = homeUrl;
  const platformUrl = pathOf('latam-platform', locale);
  // The agent pages share one folder (e.g. /pt-br/ia/): it highlights the AI menu.
  const agentsFolder = stripSlash(
    pathOf('latam-agent-tracking', locale).replace(/[^/]+\/$/, '')
  );

  const items = [
    {
      label: nav.ai.label,
      highlight: agentsFolder,
      highlightHome: true,
      cta: null,
      size: 'small',
      submenu: [
        { label: nav.ai.items.overview, url: aiUrl },
        {
          label: nav.ai.items.document_collection,
          url: pathOf('latam-agent-document-collection', locale),
        },
        {
          label: nav.ai.items.tracking,
          url: pathOf('latam-agent-tracking', locale),
        },
        {
          label: nav.ai.items.tender_failures,
          url: pathOf('latam-agent-tender-failures', locale),
        },
        {
          label: nav.ai.items.load_audits,
          url: pathOf('latam-agent-load-audits', locale),
        },
        {
          label: nav.ai.items.claims,
          url: pathOf('latam-agent-claims', locale),
        },
        {
          label: nav.ai.items.scheduling,
          url: pathOf('latam-agent-scheduling', locale),
        },
      ],
    },
    {
      label: nav.platform.label,
      highlight: stripSlash(platformUrl),
      cta: null,
      submenu: [
        {
          label: nav.platform.items.platform.label,
          description: nav.platform.items.platform.description,
          url: platformUrl,
        },
        {
          label: nav.platform.items.loadsmart_ai.label,
          description: nav.platform.items.loadsmart_ai.description,
          url: aiUrl,
        },
        {
          label: nav.platform.items.dock_gate_yard.label,
          description: nav.platform.items.dock_gate_yard.description,
          url: getOpendockUrl(locale),
          external: true,
        },
      ],
    },
    {
      label: nav.about.label,
      highlight: stripSlash(pathOf('latam-about', locale)),
      cta: null,
      size: 'small',
      submenu: [
        {
          label: nav.about.items.who_we_are,
          url: pathOf('latam-about', locale),
        },
        { label: nav.about.items.careers, url: '/careers/' },
        {
          label: nav.about.items.resources,
          url: 'https://content.loadsmart.com/',
          external: true,
        },
        {
          label: nav.about.items.blog,
          url: 'https://blog.loadsmart.com/',
          external: true,
        },
        {
          label: nav.about.items.community,
          url: 'https://community.loadsmart.com/hc/en-us',
          external: true,
        },
        {
          label: nav.about.items.api,
          url: 'https://developer.loadsmart.com/docs/',
          external: true,
        },
      ],
    },
    {
      label: nav.contact,
      highlight: stripSlash(pathOf('latam-contact', locale)),
      cta: { url: pathOf('latam-contact', locale), external: false },
      submenu: [],
    },
    {
      label: nav.login,
      highlight: '/login',
      cta: { url: '/login/', external: false },
      submenu: [],
    },
    {
      label: nav.signup,
      highlight: '/signup',
      cta: { url: '/signup/', external: false },
      submenu: [],
    },
  ];

  const legalAgreementPath =
    getPathFor('legal-subscription-agreement', LEGACY_LEGAL_LOCALE[locale]) ||
    '/subscription-and-professional-services-agreement/';

  const legal = [
    {
      label: footer.legal.user_agreement,
      cta: { url: '/user-agreement/', external: false },
    },
    {
      label: footer.legal.subscription_agreement,
      cta: { url: legalAgreementPath, external: false },
    },
    ...(footer.legal.managed_transportation_agreement
      ? [
          {
            label: footer.legal.managed_transportation_agreement,
            cta: {
              url: '/managed-transportation-master-services-agreement/',
              external: false,
            },
          },
        ]
      : []),
    {
      label: footer.legal.privacy_policy,
      cta: { url: '/privacy-policy/', external: false },
    },
    {
      label: footer.legal.security,
      cta: { url: 'https://lp.loadsmart.com/security', external: true },
    },
  ];

  return {
    nav: { homeUrl, homeAriaLabel: common.home_aria_label, items },
    footer: {
      homeUrl,
      homeAriaLabel: common.home_aria_label,
      columns: [
        {
          label: footer.platform,
          cta: { url: platformUrl },
          submenu: [
            { label: nav.platform.items.platform.label, url: platformUrl },
            { label: nav.platform.items.loadsmart_ai.label, url: aiUrl },
            {
              label: nav.platform.items.dock_gate_yard.label,
              url: getOpendockUrl(locale),
              external: true,
            },
          ],
        },
        // The copy document names a Carriers column but lists no items for it.
        // Empty columns are filtered out below, so it stays hidden until its
        // items are specified (add `submenu` entries here to bring it back).
        { label: footer.carriers, cta: { url: '/carrier/' }, submenu: [] },
        {
          label: footer.company,
          cta: null,
          submenu: [
            {
              label: nav.about.items.who_we_are,
              url: pathOf('latam-about', locale),
            },
            { label: nav.about.items.careers, url: '/careers/' },
            {
              label: nav.about.items.resources,
              url: 'https://content.loadsmart.com/',
              external: true,
            },
            {
              label: nav.about.items.blog,
              url: 'https://blog.loadsmart.com/',
              external: true,
            },
            {
              label: nav.about.items.community,
              url: 'https://community.loadsmart.com/hc/en-us',
              external: true,
            },
            {
              label: nav.about.items.api,
              url: 'https://developer.loadsmart.com/docs/',
              external: true,
            },
            { label: footer.integrations, url: '/integrations/' },
          ],
        },
      ].filter((column) => column.submenu.length > 0),
      legal,
      copyright: footer.copyright,
    },
  };
}
